import { useState, useEffect, useCallback, useRef } from "react";
import { useSignMessage } from "wagmi";
import { DERIVATION_MESSAGE } from "../local/actions.ts";
import {
  EspectroAccountManager,
  type EspectroAccount,
  type EnterParams,
  type EnterResult,
  type EVMTransactionRequest,
  type ExitParams,
  type ExitResult,
  type EnterStep,
  type ExitStep,
  type SyncProgress,
} from "../lit/account.ts";
import type { BalanceResult } from "../lit/actions.ts";
import type { TrackResponse } from "../swapkit/index.ts";
import type { UTXO } from "../zcash/lightwalletd.ts";
import {
  type EspectroOperation,
  loadOperation,
  clearOperation,
  abandonOperation,
  isTerminalStage,
} from "../lit/operation.ts";

interface AccountState {
  account: EspectroAccount | null;
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;
}

export function useEspectroAccount() {
  const managerRef = useRef(new EspectroAccountManager());

  const [state, setState] = useState<AccountState>({
    account: null,
    isLoading: true,
    isCreating: false,
    error: null,
  });

  const [enterStep, setEnterStep] = useState<EnterStep | null>(null);
  const [exitStep, setExitStep] = useState<ExitStep | null>(null);
  const [operation, setOperation] = useState<EspectroOperation | null>(() => {
    const op = loadOperation();
    return op && !isTerminalStage(op.stage) ? op : null;
  });

  useEffect(() => {
    const restored = managerRef.current.loadAccount();
    setState({
      account: restored,
      isLoading: false,
      isCreating: false,
      error: null,
    });
  }, []);

  // True when account metadata is loaded from localStorage but the seed is not
  // yet in memory (expected on every page load — requires a wallet signature to restore).
  const needsReauth = !state.isLoading && !!state.account && !state.account.seedB64;

  /** Re-read the operation from storage and run reconciliation. */
  const reconcile = useCallback(async () => {
    try {
      const op = await managerRef.current.reconcileOperation();
      if (op && isTerminalStage(op.stage)) {
        setOperation(null);
      } else {
        setOperation(op);
      }
      return op;
    } catch {
      return null;
    }
  }, []);

  const refreshOperation = useCallback(() => {
    const op = loadOperation();
    if (op && isTerminalStage(op.stage)) {
      setOperation(null);
    } else {
      setOperation(op);
    }
  }, []);

  const { signMessageAsync } = useSignMessage();

  const createAccount = useCallback(async () => {
    setState((s) => ({ ...s, isCreating: true, error: null }));
    try {
      // Derive ZEC seed deterministically from the connected ETH wallet.
      // Same wallet → same signature → same ZEC addresses, even after cache clear.
      // Signature is required — the Zcash seed is derived deterministically from it.
      // Same wallet + same message = same Zcash keys, even after localStorage is cleared.
      const ethSignature = await signMessageAsync({ message: DERIVATION_MESSAGE });
      const account = await managerRef.current.createAccount(ethSignature);
      setState({ account, isLoading: false, isCreating: false, error: null });
      return account;
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to create account";
      setState((s) => ({ ...s, isCreating: false, error: msg }));
      throw err;
    }
  }, [signMessageAsync]);

  const logout = useCallback(async () => {
    await managerRef.current.logout();
    setOperation(null);
    setState({
      account: null,
      isLoading: false,
      isCreating: false,
      error: null,
    });
  }, []);

  const enter = useCallback(
    async (
      params: Omit<EnterParams, "onProgress" | "signAndSend"> & {
        signAndSend?: (tx: EVMTransactionRequest) => Promise<string>;
        preBalanceZats?: number;
      },
    ): Promise<EnterResult> => {
      setEnterStep("quoting");
      try {
        const result = await managerRef.current.enter({
          ...params,
          onProgress: setEnterStep,
        });
        refreshOperation();
        return result;
      } catch (err) {
        setEnterStep(null);
        refreshOperation();
        throw err;
      }
    },
    [refreshOperation],
  );

  const exit = useCallback(
    async (
      params: Omit<ExitParams, "onProgress"> & {
        preBalanceZats?: number;
      },
    ): Promise<ExitResult> => {
      setExitStep("scanning");
      try {
        const result = await managerRef.current.exit({
          ...params,
          onProgress: setExitStep,
        });
        refreshOperation();
        return result;
      } catch (err) {
        setExitStep(null);
        refreshOperation();
        throw err;
      }
    },
    [refreshOperation],
  );

  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);

  const [orchardBalance, setOrchardBalance] = useState<{ zec: number; noteCount: number } | null>(null);
  const [orchardScanStatus, setOrchardScanStatus] = useState<"idle" | "scanning" | "found" | "not_found" | "error">("idle");
  const [orchardScanError, setOrchardScanError] = useState<string | null>(null);
  const [orchardScanProgress, setOrchardScanProgress] = useState<SyncProgress | null>(null);
  const [recoverStep, setRecoverStep] = useState<string | null>(null);
  const [recoverError, setRecoverError] = useState<string | null>(null);
  const [recoverResult, setRecoverResult] = useState<string | null>(null);

  const getBalance = useCallback(async (): Promise<BalanceResult> => {
    return managerRef.current.getShieldedBalance(undefined, setSyncProgress);
  }, []);

  const getTransparentBalance = useCallback(
    async (): Promise<{ utxos: UTXO[]; totalZec: number }> => {
      return managerRef.current.getTransparentBalance();
    },
    [],
  );

  const recoverShield = useCallback(
    async (
      onProgress?: (step: string) => void,
    ): Promise<{ txid: string }> => {
      const result = await managerRef.current.recoverShield(onProgress);
      refreshOperation();
      return result;
    },
    [refreshOperation],
  );

  const recoverReturnToOrigin = useCallback(
    async (
      params: {
        destinationAsset: string;
        destinationAddress: string;
        onProgress?: (step: string) => void;
      },
    ): Promise<{ txid: string; swapId: string }> => {
      const result = await managerRef.current.recoverReturnToOrigin(params);
      refreshOperation();
      return result;
    },
    [refreshOperation],
  );

  const recoverOrchardToAddress = useCallback(
    async (params: {
      destinationTAddr: string;
      onProgress?: (step: string) => void;
    }): Promise<{ txid: string }> => {
      const result = await managerRef.current.recoverOrchardToAddress(params);
      refreshOperation();
      return result;
    },
    [refreshOperation],
  );

  const scanOrchard = useCallback(async (txHash?: string) => {
    setOrchardScanStatus("scanning");
    setOrchardScanError(null);
    setOrchardScanProgress(null);
    try {
      const result = await managerRef.current.scanOrchardBalance({
        blocks: 3000,
        txHash,
        onProgress: setOrchardScanProgress,
      });
      setOrchardBalance({ zec: result.balanceZec, noteCount: result.noteCount });
      setOrchardScanStatus(result.noteCount > 0 ? "found" : "not_found");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Orchard scan failed";
      setOrchardScanError(msg);
      setOrchardScanStatus("error");
    }
  }, []);

  const recoverFunds = useCallback(async (params: {
    destinationAsset: string;
    destinationAddress: string;
  }): Promise<void> => {
    setRecoverStep("checking");
    setRecoverError(null);
    setRecoverResult(null);
    try {
      const result = await managerRef.current.recoverFunds({
        ...params,
        onProgress: setRecoverStep,
      });
      setRecoverResult(result.txid);
      setRecoverStep("done");
      refreshOperation();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Recovery failed";
      setRecoverError(msg);
      setRecoverStep(null);
    }
  }, [refreshOperation]);

  const retryBroadcast = useCallback(
    async (rawTx: string): Promise<string> => {
      const hash = await managerRef.current.retryBroadcast(rawTx);
      refreshOperation();
      return hash;
    },
    [refreshOperation],
  );

  const resumeExitSwapTracking = useCallback(
    async (onStatus?: (status: string) => void): Promise<void> => {
      await managerRef.current.resumeExitSwapTracking(onStatus);
      refreshOperation();
    },
    [refreshOperation],
  );

  const dismissOperation = useCallback(() => {
    const op = loadOperation();
    if (op) {
      abandonOperation(op);
    }
    clearOperation();
    setOperation(null);
  }, []);

  const resetSteps = useCallback(() => {
    setEnterStep(null);
    setExitStep(null);
  }, []);

  const trackSwap = useCallback(
    async (hash: string, chainId: string): Promise<TrackResponse> => {
      return managerRef.current.trackSwap(hash, chainId);
    },
    [],
  );

  return {
    ...state,
    needsReauth,
    enterStep,
    exitStep,
    syncProgress,
    operation,
    createAccount,
    logout,
    enter,
    exit,
    getBalance,
    getTransparentBalance,
    recoverShield,
    recoverReturnToOrigin,
    recoverOrchardToAddress,
    retryBroadcast,
    resumeExitSwapTracking,
    reconcile,
    refreshOperation,
    dismissOperation,
    resetSteps,
    trackSwap,
    orchardBalance,
    orchardScanStatus,
    orchardScanError,
    orchardScanProgress,
    scanOrchard,
    recoverStep,
    recoverError,
    recoverResult,
    recoverFunds,
  };
}
